import { useContentMotion } from "../lib/useContentMotion";
import { navigateWorkbench } from "../lib/studioNavigation";
import { agentIdFrom } from "../lib/base";
/**
 * Hosts a plugin page without owning its task data. The host relays declared
 * reads/actions, retains tab-local working state, reports actual action results,
 * and pushes theme, size and visibility. A page whose files move is replaced as
 * soon as they do -- its draft has a durable home and comes back on the next
 * handshake -- except while an operation of the person's is still in flight,
 * which is the one thing a replacement could lose.
 *
 * The sandbox allows scripts only. An opaque origin is shared by all guests,
 * so the relay identifies the peer by its iframe window, not its origin string.
 */

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError, type CommandStatus, type RecordRead } from "../lib/api";
import { approvalsFeed } from "../lib/approvalsFeed";
import { commandFeed } from "../lib/commandFeed";
import { workFeed } from "../lib/workFeed";
import { readDraft, useDraft, writeDraft } from "../lib/drafts";
import { displayScreenPage, flushScreenIdentity, holdOnScreen, onScreenIdentityReady, reportScreenContent } from "../lib/screen";
import { withDeadline } from "../lib/deadline";
import { actionFromPress, readFromAnswer, stateFrame,
  type SurfaceActionState, type SurfaceDraft, type SurfaceRead, type SurfaceSnapshot } from "./surfaceState";
import { effectiveTheme } from "../lib/appearance";
export { effectiveTheme } from "../lib/appearance";
import { surfaceDiagnostic } from "../lib/surfaceResources";
import { ErrorState, LoadingState } from "../shell/ui";
import { responseConversationHref } from "../shell/ownerShell";
import type { PanelBlock } from "./spec";
import { buildSrcdoc, entryPath, mediaBase, uiBase } from "./surfaceBridge";
import { newEmbeddedFlowToken, useEmbeddedSurfaceFlow } from "./embeddedSurfaceFlow";
import { SurfaceAction, type SurfaceSubmission } from "./SurfaceAction";
import { SurfaceRepair, type SurfaceFault } from "./SurfaceRepair";
import { prepareSurfaceIntent, SurfaceIntentComposer, SurfaceConversationFocus } from "./SurfaceIntent";
import { savedNavigationContext, workspaceNavigation } from "../studio/WorkContext";
import { recordLinkHref } from "./recordLinks";
import { surfaceNavigationContext } from "./surfaceNavigation";
import { SurfaceCardHead } from "./SurfaceCardHead";
import { BusyButton } from "../shell/BusyButton";
import { useWorkTool } from "../shell/workTools";
import "./SurfaceHost.css";

/** The methods the guest may call. `ready` is answered here plus on the server. */
const GUEST_METHODS = [
  "surface.ready",
  "surface.act",
  "surface.tail",
  "surface.rendered",
  "surface.complete",
  "surface.error",
  "surface.save_state",
  "surface.intent",
  "surface.open",
  "surface.update_model_context",
  "surface.send_message",
  "surface.focus_chat",
  // The page runtime's three (`liquid-page-runtime.md` §1-§2). None of them
  // reaches the core: they are this host handing the page the projection it
  // already holds, reading the block's own declared reads again, and taking
  // the person to a card that lives in the shell.
  "surface.subscribe",
  "surface.refresh",
  "surface.approvals_open",
  // A page in the sandbox cannot open a window of its own: no popups, no top
  // navigation (§7.130). A link it shows is opened here, in a new tab, and
  // only when it is a web address.
  "surface.open_link",
];

/** A web address a page asked to open, or null for anything that is not one. */
function webAddress(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * Panel key -> the signature of that panel's `ui/` tree, as the server last
 * saw it (the `surfaces` stream event). Empty by default, so a surface drawn
 * outside the shell — a test, a page that never subscribed — simply never
 * reloads rather than needing a provider.
 */
export const SurfaceVersions = createContext<Record<string, string>>({});
export const SurfaceVisibility = createContext(true);
export const PanelDeveloper = createContext(false);
function requestValue(value: unknown): string {
  return JSON.stringify(value, (_key, item) => item && typeof item === "object" && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
}
const SETTLED = ["completed", "failed", "stopped"];
/** One press this page made while it was mounted here, under the name the page gave it. */
interface Press {
  method: "surface.act" | "surface.send_message";
  params: Record<string, unknown>;
  label: string;
  obligation_id?: string;
  /** Its answer never came back: it may or may not have arrived. */
  unknown?: boolean;
  at: number;
}
/** A press of this page still under way, as `surface.ready` reads it off the record. */
interface PendingPress {
  command_id: string;
  obligation_id?: string | null;
  state?: string;
  action?: string;
  params?: Record<string, unknown>;
  object?: unknown;
  text?: string;
}
interface SurfaceDocument { srcdoc: string; generation: number; version: string; scopedResources: boolean; flowToken?: string }

export function SurfaceHost({
  panelKey,
  block,
  index,
  onToast,
  context = {},
  pageTitle,
  viewContext = context,
  onStateSaved,
  renderToolbar,
  compact = false,
  presentation = "page",
  record,
  toolsInBar = false,
}: {
  panelKey: string;
  block: PanelBlock;
  index: number;
  onToast: (message: string, tone: "good" | "critical") => void;
  context?: Record<string, string>;
  /** The registered page name, and optional current selection coordinates. */
  pageTitle?: string;
  viewContext?: Record<string, string>;
  /**
   * The record of the call this page sits under, when it is a card's page
   * (`records.tsx`): handed on `pa.ready()` and kept current in the snapshot,
   * so the page finds its own object in it. Absent on every other page.
   */
  record?: RecordRead;
  /** The declared navigation coordinate for restoring this UI state; it never changes the read/action binding. */
  onStateSaved?: (state: unknown) => Record<string, string> | void;
  /** Place shared navigation beside this frame's own reload control. */
  renderToolbar?: (reload: ReactNode) => ReactNode;
  /**
   * Draw this face inside a tile rather than on a page of its own.
   *
   * The head keeps what a tile can say -- the face's name and whose Agent it is
   * -- and drops the sentence and the reload control, which a tile has no room
   * for; the frame takes the height of the box it is given instead of a page's
   * own. The page is told, in the `surface.size` it already receives, so a page
   * that lays itself out differently for a tile can: `{width, height, compact}`.
   */
  compact?: boolean;
  /** An overview has its own heading; failures and recovery stay visible here. */
  presentation?: "page" | "embedded";
  /** The Agent's bar carries the reload and the head's last-change record (`shell/workTools.tsx`); this page draws neither. */
  toolsInBar?: boolean;
}) {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  const [doc, setDoc] = useState<SurfaceDocument | null>(null);
  const [assembled, setAssembled] = useState<SurfaceDocument | null>(null);
  const flowHeight = useEmbeddedSurfaceFlow(frame, doc?.flowToken);
  const documentGeneration = useRef(0);
  const loaded = useRef(false);
  const stateSaved = useRef(onStateSaved);
  stateSaved.current = onStateSaved;
  const modelContextSequence = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const reports = useRef<Record<string, CommandStatus>>({});
  const [appearance, setAppearance] = useState(() => ({ theme: effectiveTheme(), palette: document.documentElement.dataset.palette, motion: document.documentElement.dataset.motion || "full" }));
  // What the server last saw this panel's `ui/` tree as. A change means the
  // page on screen is stale (`service.py` `scan_surface_files`).
  const version = useContext(SurfaceVersions)[panelKey] ?? "";
  const visible = useContext(SurfaceVisibility);
  useContentMotion(box, `${panelKey}:${doc?.generation ?? ""}`, visible && !loading && doc !== null);
  const currentVisibility = useRef(visible);
  currentVisibility.current = visible;
  const developer = useContext(PanelDeveloper);
  const prepareIntent = useContext(SurfaceIntentComposer);
  const focusChat = useContext(SurfaceConversationFocus);
  const handshake = useRef<Record<string, unknown>>({});
  const [appliedVersion, setAppliedVersion] = useState("");
  const requestedVersion = useRef(version);
  const [reloads, setReloads] = useState(0);
  const contextKey = JSON.stringify(context);
  const viewContextKey = requestValue(viewContext);
  const currentViewContext = useRef(viewContextKey);
  currentViewContext.current = viewContextKey;
  const displayedSelection = useRef<{ showing: string; context: string; visible: boolean } | null>(null);
  const draftKey = `surface:${panelKey}:${index}:${contextKey}`;
  // A live document can move its address while its read/action binding stays at entry. Keep its current UI state
  // in memory, and store each address's drafts under that address instead of overwriting the entry's old draft.
  const liveUiState = useRef<{ key: string; value: unknown } | null>(null);
  const readUiState = useCallback((fallback: unknown = null) => liveUiState.current?.key === draftKey
    ? liveUiState.current.value : readDraft(draftKey, null) ?? fallback, [draftKey]);
  // This showing of the page: one per mount, document and coordinate. What
  // the page tells the Agent is stamped with it and carried only while it is
  // on screen (`lib/screen.ts`), so a page reloaded, mounted again or moved to
  // another object starts with nothing said until it says it again.
  const showing = useMemo(() => crypto.randomUUID(), [contextKey, doc?.generation]);
  const currentShowing = useRef(showing);
  currentShowing.current = showing;
  const contextRecovery = useRef(0);
  const [fault, saveFault] = useDraft<SurfaceFault | null>(`${draftKey}:fault`, null);
  const liveFault = useRef(fault);
  liveFault.current = fault;
  const setFault = useCallback((next: SurfaceFault | null) => { liveFault.current = next; saveFault(next); }, [saveFault]);
  const faultGeneration = useRef<number | null>(null);
  // A refused session is a session problem; one file of the page not arriving
  // is not. A page whose own entry file is not where the page said is a page
  // this Agent can fix, and telling the Owner to log in again is a dead end in
  // front of the one fault the repair path exists for (D405).
  const signedOut = fault?.detail.status === 401 || fault?.detail.status === 403;
  // A subresource that did not arrive is still a reload before it is a paid
  // repair request: the file is on disk, something between here and it failed.
  const resourceFault = !signedOut && fault?.detail.kind === "resource";
  const shownVersion = useRef(appliedVersion);
  shownVersion.current = doc?.version || appliedVersion;
  // The status lines beside the page: this mount's presses, and on a new
  // mount the ones `surface.ready` says are still under way.
  const [submissions, setSubmissions] = useState<SurfaceSubmission[]>([]);
  const savedSubmissions = useRef(submissions);
  const activeDraft = useRef(draftKey);
  activeDraft.current = draftKey;
  const presses = useRef(new Map<string, Press>());
  // Presses whose answer has not come back yet. The document is not replaced
  // under them: the answer belongs to the page that is waiting for it.
  const inflight = useRef(0);
  const [pendingCount, setPendingCount] = useState(0);
  // Bumped when something this face started has actually finished, so the head
  // re-reads what is now a fact. Not a poll, and not the moment the submission
  // becomes *known*: an obligation row exists as soon as it is projected, and a
  // head that re-read then would read `null` and never ask again.
  const [headVersion, setHeadVersion] = useState(0);
  // The first signature establishes the baseline; there is nothing to reload
  // onto before a document has loaded once.
  useEffect(() => { if (loaded.current && !appliedVersion && version) setAppliedVersion(version); }, [version, appliedVersion]);

  const origin = window.location.origin;
  const ui = useMemo(() => uiBase(origin, panelKey), [origin, panelKey]);
  const media = useMemo(() => mediaBase(origin, panelKey), [origin, panelKey]);
  const entry = useMemo(() => entryPath(block.body ?? ""), [block.body]);

  // The page itself. Fetched as text rather than pointed at with `src`, because
  // `srcdoc` is what makes the origin opaque *and* lets the policy be injected;
  // a `src` on the same host would share this document's origin unless the
  // sandbox stripped it, and the CSP would have to travel as a header on a
  // response the plugin does not control.
  useEffect(() => {
    let live = true;
    const resourceRead = new AbortController();
    setError(null);
    setLoading(true);
    const targetVersion = requestedVersion.current;
    (async () => {
      try {
        const page = await api.panelUiFile(panelKey, entry, resourceRead.signal);
        if (!live) return;
        const flowToken = presentation === "embedded" ? newEmbeddedFlowToken() : undefined;
        setAssembled({
          generation: documentGeneration.current + 1,
          version: targetVersion,
          scopedResources: Boolean(page.resources),
          flowToken,
          srcdoc: buildSrcdoc({
            panelKey,
            blockIndex: index,
            html: page.html,
            ui: page.resources?.ui ?? ui,
            media: page.resources?.media ?? media,
            mediaOrigin: page.resources?.mediaOrigin,
            hostLifecycle: true,
            embeddedFlowToken: flowToken,
            theme: effectiveTheme(),
            palette: document.documentElement.dataset.palette, motion: document.documentElement.dataset.motion || "full",
          }),
        });
      } catch (err) {
        if (live) {
          setError(err);
          setLoading(false);
          const failed = ApiError.fromUnknown(err);
          setFault({ detail: { kind: failed.code === "resource_read_timeout" ? "resource" : "load",
            message: failed.detail, source: entry, status: failed.status }, version: shownVersion.current });
        }
      }
    })();
    return () => {
      live = false;
      resourceRead.abort();
    };
  }, [panelKey, entry, index, ui, media, reloads, presentation]);

  // An action may start while the HTML is being fetched. Keep its document
  // until the answer has come back to the page that is waiting for it.
  useEffect(() => {
    if (!assembled || pendingCount) return;
    documentGeneration.current = assembled.generation;
    handshake.current = {};
    loaded.current = false;
    setDoc(assembled);
    setAssembled(null);
  }, [assembled, pendingCount]);

  const showSubmissions = useCallback((next: SurfaceSubmission[]) => {
    savedSubmissions.current = next;
    setSubmissions(next);
  }, []);

  const reload = () => {
    if (loading || inflight.current) return false;
    requestedVersion.current = version;
    if (fault && fault.detail.kind !== "error" && fault.detail.kind !== "unhandledrejection") setFault(null);
    setLoading(true);
    setReloads(n => n + 1);
    return true;
  };
  const reloadNow = useRef(reload);
  reloadNow.current = reload;

  // Read data and media URLs must come from the same handshake. A changed
  // scope replaces the document once no press is waiting for its answer; a
  // press keeps the scope it was sent with and is never sent again for this.
  const [scopeRefresh, setScopeRefresh] = useState<{ scope: string; generation: number; context: string } | null>(null);
  const attemptedScopeRefresh = useRef("");
  const readScopeCurrent = useCallback((result: unknown) => {
    const scope = (result as { scope?: unknown } | null)?.scope;
    if (!scope || !handshake.current.scope || requestValue(scope) === requestValue(handshake.current.scope)) return true;
    setScopeRefresh({ scope: requestValue(scope), generation: documentGeneration.current, context: contextKey });
    return false;
  }, [contextKey]);
  useEffect(() => {
    if (!visible || !scopeRefresh || scopeRefresh.generation !== documentGeneration.current || scopeRefresh.context !== contextKey) return;
    if (loading || pendingCount) return;
    const attempt = requestValue([panelKey, index, contextKey, scopeRefresh.scope]);
    if (attemptedScopeRefresh.current === attempt) return;
    if (reloadNow.current()) attemptedScopeRefresh.current = attempt;
  }, [scopeRefresh, panelKey, index, contextKey, loading, pendingCount, visible]);

  /**
   * Hot reload: the page is replaced as soon as its files move.
   *
   * It used to wait for a click, and the reason was real -- a guest's own
   * in-memory state cannot be snapshotted, so replacing the document threw it
   * away. What changed is that the state has somewhere to live: a page saves
   * with `pa.saveState`, the draft is kept in this tab *and* beside the
   * plugin's own data (s2, D404), and the next handshake hands it straight
   * back. So the click was asking a person to approve a loss that no longer
   * happens, on a page they had no way to tell was stale.
   *
   * One thing still waits, the one a reload could actually lose: an operation
   * of theirs whose answer has not come back. The banner below keeps the click
   * for exactly that.
   *
   * One attempt per signature. A file change that cannot be fetched leaves the
   * retry to the person instead of re-asking forever (`error` below).
   */
  const autoApplied = useRef("");
  useEffect(() => {
    if (!version || !appliedVersion || version === appliedVersion) return;
    if (loading || pendingCount) return;
    if (autoApplied.current === version) return;
    autoApplied.current = version;
    reloadNow.current();
  }, [version, appliedVersion, loading, pendingCount]);

  /**
   * One box, one number.
   *
   * The observer reports a content box in fractions; `clientWidth` rounds and
   * counts padding. Pushing one on load and the other on resize told a page
   * 1173 and then 1174 for a box that never moved, and left the page to decide
   * which of them it was in. The observer's measurement is the one, and
   * `clientWidth` only stands in before it has ever fired.
   */
  const observed = useRef<{ width: number; height: number } | null>(null);
  const measuredBox = useCallback(
    () => observed.current ?? { width: box.current?.clientWidth ?? 0, height: box.current?.clientHeight ?? 0 },
    [],
  );

  const post = useCallback((message: Record<string, unknown>) => {
    // "*" is the only target that works: the guest's origin is opaque, so there
    // is no origin string to name. It is safe here because the only window
    // reachable through `contentWindow` is that iframe.
    frame.current?.contentWindow?.postMessage({ __pa: 1, ...message }, "*");
  }, []);
  const contextFlushes = useRef(new Map<string, { generation: number; finish: (available: boolean) => void }>());
  const flushModelContext = useCallback(() => {
    if (!currentVisibility.current || !frame.current?.contentWindow) return Promise.resolve(false);
    const nonce = crypto.randomUUID();
    const answer = new Promise<boolean>(resolve => {
      contextFlushes.current.set(nonce, { generation: documentGeneration.current, finish: resolve });
    });
    post({ method: "surface.flush_model_context", params: { nonce } });
    return withDeadline(answer, false).finally(() => { contextFlushes.current.delete(nonce); });
  }, [post]);

  // -- the page's own state (`liquid-page-runtime.md` §1) -------------------
  //
  // One snapshot, assembled from what this host already holds: the block's
  // declared reads as they were last answered, the presses it relayed and the
  // statuses the projection pushed for them, and the drafts it wrote. The
  // page reads it; nothing here is a second copy of any of it, and nothing is
  // sent twice (`stateFrame`).
  const said = useRef<SurfaceSnapshot | null>(null);
  const reads = useRef<SurfaceRead[]>([]);
  const drafts = useRef<Record<string, SurfaceDraft>>({});
  const subscribed = useRef(false);
  const readArguments = useRef<Record<string, Record<string, unknown>>>({});
  // null preserves legacy all-read subscriptions; [] explicitly follows none.
  const readIndices = useRef<number[] | null>(null);
  const staleWhileHidden = useRef(false);
  /** How many times this page has been moved to another object; an older frame is not about this one. */
  const contextRevision = useRef(0);
  const shownContext = useRef(contextKey);
  const declaredReads = useMemo(() => block.reads ?? [], [block.reads]);
  const sitsOn = useRef(record);
  sitsOn.current = record;

  const snapshot = useCallback((): SurfaceSnapshot => ({
    context: JSON.parse(contextKey),
    context_revision: contextRevision.current,
    theme: effectiveTheme(),
    palette: document.documentElement.dataset.palette, motion: document.documentElement.dataset.motion || "full",
    size: { ...measuredBox(), compact },
    visible: currentVisibility.current,
    ...(sitsOn.current === undefined ? {} : { record: sitsOn.current }),
    reads: reads.current,
    actions: Object.fromEntries([...presses.current]
      .filter(([, press]) => press.method === "surface.act")
      .map(([command, press]): [string, SurfaceActionState] => [command,
        actionFromPress(command, press, press.obligation_id ? reports.current[press.obligation_id] : undefined)])),
    drafts: drafts.current,
  }), [contextKey, compact, measuredBox]);

  const pushState = useCallback(() => {
    if (!subscribed.current) return;
    const next = snapshot();
    const frame = stateFrame(said.current, next);
    said.current = next;
    if (frame) post({ method: "surface.state", params: frame });
  }, [snapshot, post]);

  /**
   * The declared reads, answered again.
   *
   * The page does not run this loop and never has a cursor of its own: a read
   * is one call through the read door (§7.182), and which slots to ask about
   * is either the page's `pa.refresh` or the action declaration's `refresh`.
   * An answer for a slot a newer call has already re-asked is dropped, so two
   * overlapping refreshes cannot leave the older one's value on screen.
   */
  const draftWrites = useRef(0);
  const settleDrafts = useCallback((keys: string[], persisted: boolean, error?: string) => {
    // The write is no longer in flight either way: landed or failed, `writing`
    // is not true of it any more, and leaving it set would draw a failure that
    // has already been decided as still "on its way" (§1).
    drafts.current = Object.fromEntries(Object.entries(drafts.current).map(([key, entry]) => {
      if (!keys.includes(key)) return [key, entry];
      const { writing: _writing, ...rest } = entry;
      return [key, { ...rest, persisted, ...(error ? { error } : {}) }];
    }));
    pushState();
  }, [pushState]);

  const readCycles = useRef(new Map<number, number>());
  const readEpoch = useRef(0);
  const readRequests = useRef(new Set<AbortController>());
  useLayoutEffect(() => {
    // The replacement document subscribes afresh. A copied selection from its
    // predecessor must not suppress its first actual read/receipt.
    subscribed.current = false;
    readIndices.current = null;
    readArguments.current = {};
    reads.current = [];
    said.current = null;
    readCycles.current.clear();
  }, [panelKey, index, doc?.generation]);
  useEffect(() => () => {
    // Only READ transport is cancelled. A write whose receipt was lost keeps
    // its original request identity and its existing unknown/recovery path.
    readEpoch.current += 1;
    readRequests.current.forEach(request => request.abort());
    readRequests.current.clear();
  }, [panelKey, index, contextKey, doc?.generation, visible]);
  const runReads = useCallback(async (indices?: number[]) => {
    if (!declaredReads.length) return;
    if (!currentVisibility.current) { staleWhileHidden.current = true; return; }
    const wanted = (indices ?? readIndices.current ?? declaredReads.map((_, at) => at))
      .filter(at => Number.isInteger(at) && at >= 0 && at < declaredReads.length);
    if (!wanted.length) return;
    const cycles = new Map(wanted.map(at => [at, (readCycles.current.get(at) ?? 0) + 1]));
    cycles.forEach((cycle, at) => readCycles.current.set(at, cycle));
    const mine = (at: number) => cycles.has(at) && readCycles.current.get(at) === cycles.get(at);
    const context = JSON.parse(contextKey);
    const revision = contextRevision.current;
    const generation = documentGeneration.current;
    const epoch = readEpoch.current;
    const request = new AbortController();
    readRequests.current.add(request);
    const current = () => currentVisibility.current && epoch === readEpoch.current
      && revision === contextRevision.current && generation === documentGeneration.current;
    try {
      const answer = await api.surfaceCall(panelKey, index, "surface.tail",
        { cursor: "", read_arguments: readArguments.current, read_indices: wanted, context }, request.signal);
      if (!current() || !readScopeCurrent(answer.result)) return;
      const answers = ((answer.result as { reads?: Record<string, unknown>[] }).reads) ?? [];
      const next = [...reads.current];
      answers.forEach((item, at) => {
        if (!mine(at)) return;
        const entry = readFromAnswer(at, item, String((declaredReads[at] as { version?: string }).version ?? ""));
        if (entry) next[at] = entry;
      });
      reads.current = next;
    } catch (err) {
      if (!current()) return;
      const failed = ApiError.fromUnknown(err);
      reads.current = declaredReads.map((read, at) => wanted.includes(at) && mine(at)
        ? { index: at, tool: String((read as { tool?: string }).tool ?? ""), state: "error" as const, error: failed.detail }
        : reads.current[at] ?? { index: at, tool: "", state: "pending" as const });
    } finally {
      readRequests.current.delete(request);
    }
    pushState();
  }, [declaredReads, panelKey, index, contextKey, pushState, readScopeCurrent]);

  /**
   * Unsolicited re-reads: nobody on the page asked for these, so they are all
   * funnelled through here rather than through `runReads` directly. Three
   * things call this -- a `work` tick, the page coming back into view, and
   * (below) nothing else, because `surface.refresh` and `refreshAfter` are
   * about a submission *this* page just made and keep calling `runReads`
   * themselves (§2 rule 8): reading twice for one landing would put two rows
   * through the read door for one press.
   *
   * One round in flight at a time. A tick that lands while a round is running
   * is not dropped and not queued per-tick -- it is remembered once, and when
   * the running round ends it runs exactly one more, so a burst of ticks
   * costs at most two rounds. No timer and no debounce clock: back-pressure
   * from the round itself is what limits it.
   *
   * A tick while the page is hidden reads nothing -- a hidden surface has
   * nowhere to show it -- and is remembered as "stale" instead; becoming
   * visible clears that and runs one round of its own (below), which is also
   * what a page with no pending tick gets simply for having been hidden and
   * come back, exactly as every legacy page's `pa.onVisibility` did.
   */
  const wantReadsRound = useRef(false);
  const wantReadsAgain = useRef(false);
  const wantReads = useCallback(() => {
    if (!subscribed.current || !declaredReads.length) return;
    if (!currentVisibility.current) { staleWhileHidden.current = true; return; }
    if (wantReadsRound.current) { wantReadsAgain.current = true; return; }
    wantReadsRound.current = true;
    void runReads().finally(() => {
      wantReadsRound.current = false;
      if (wantReadsAgain.current) {
        wantReadsAgain.current = false;
        wantReadsNow.current();
      }
    });
  }, [declaredReads.length, runReads]);
  const wantReadsNow = useRef(wantReads);
  wantReadsNow.current = wantReads;

  useEffect(() => workFeed.subscribe(() => wantReadsNow.current()), []);

  const resumeEntryRead = useRef(false);
  const wasVisible = useRef(visible);
  useEffect(() => {
    const cameIntoView = visible && !wasVisible.current;
    wasVisible.current = visible;
    // The old request may still be in flight at the resume edge. Keep one
    // recovery read until it fails; a successful document needs no retry.
    if (cameIntoView && doc === null) resumeEntryRead.current = true;
    if (doc !== null) resumeEntryRead.current = false;
    if (visible && resumeEntryRead.current && error && !loading && !pendingCount) {
      if (reloadNow.current()) resumeEntryRead.current = false;
    }
    if (!cameIntoView) return;
    staleWhileHidden.current = false;
    wantReadsNow.current();
  }, [visible, doc, error, loading, pendingCount]);

  /** A press has its obligation: it gets its status line, and the page hears its receipt. */
  const accept = useCallback((command: string, obligation: string) => {
    const press = presses.current.get(command);
    if (!press) return;
    presses.current.set(command, { ...press, obligation_id: obligation, unknown: false });
    if (!savedSubmissions.current.some(item => item.id === obligation)) {
      showSubmissions([...savedSubmissions.current, { id: obligation, label: press.label,
        ...(press.method === "surface.send_message" ? { source: "surface_intent" as const } : {}) }]);
    }
    post({ method: "surface.receipt", params: { command_id: command, obligation_id: obligation } });
    const status = reports.current[obligation];
    if (status) post({ method: "surface.action", params: { ...status, command_id: command } });
    pushState();
  }, [showSubmissions, post, pushState]);
  const acceptNow = useRef(accept);
  acceptNow.current = accept;
  // A press whose answer was lost is named by the `command` frame that binds
  // it, so it turns into its real state as soon as the record shows it.
  useEffect(() => commandFeed.subscribe(frame => {
    const press = frame.command_id ? presses.current.get(frame.command_id) : undefined;
    if (press && !press.obligation_id && frame.obligation_id) acceptNow.current(frame.command_id, frame.obligation_id);
  }), []);

  // A new document can load while its Owner connection is recovering. Keep
  // its one-shot handshake pending until visible; never queue an operation.
  const deferredReady = useRef(new Map<string, { generation: number; source: Window; event: MessageEvent }>());
  const resumeReady = useRef<(event: MessageEvent) => void>(() => {});
  useEffect(() => () => {
    for (const [id, waiting] of deferredReady.current) {
      if (waiting.generation === doc?.generation) deferredReady.current.delete(id);
    }
  }, [doc?.generation]);

  // The relay.
  useEffect(() => {
    async function onMessage(event: MessageEvent) {
      const data = event.data as
        | { __pa?: number; id?: string; method?: string; params?: Record<string, unknown> }
        | null;
      if (!data || data.__pa !== 1 || !data.method) return;
      if (event.source !== frame.current?.contentWindow) return;
      if (data.method === "surface.model_context_flushed") {
        const flush = contextFlushes.current.get(String(data.params?.nonce ?? ""));
        if (flush?.generation === documentGeneration.current) flush.finish(data.params?.available === true);
        return;
      }
      if (!data.id) return;
      const source = event.source as Window;
      const generation = documentGeneration.current;
      const messageShowing = currentShowing.current;
      const current = () => generation === documentGeneration.current && source === frame.current?.contentWindow;
      const reply = (message: Record<string, unknown>) => {
        // A model observation belongs to one showing. A business press keeps
        // its original receipt across same-document navigation; ready renews
        // under its original id below when that navigation changes its draft.
        if (current() && (data.method !== "surface.update_model_context" || messageShowing === currentShowing.current)
            && (data.method !== "surface.ready" || activeDraft.current === draftKey))
          source.postMessage({ __pa: 1, id: data!.id, ...message }, "*");
      };
      if (!GUEST_METHODS.includes(data.method)) {
        reply({ error: `桥上没有 ${data.method} 这个方法。` });
        return;
      }
      const recovery = data.method === "surface.error" ? surfaceDiagnostic(data.params?.detail) : null;
      if (recovery && typeof recovery === "object" && (recovery as Record<string, unknown>).kind === "resource_recovered") {
        if (liveFault.current?.detail.kind === "resource" && liveFault.current.detail.source === (recovery as Record<string, unknown>).source) setFault(null);
        reply({ result: { cleared: true } });
        return;
      }
      if (!currentVisibility.current && data.method !== "surface.save_state") {
        if (data.method === "surface.ready") {
          deferredReady.current.set(data.id, { generation, source, event });
          return;
        }
        reply({ error: "界面已暂停，连接恢复后可继续。", code: "surface_paused" });
        return;
      }
      try {
        if (data.method === "surface.focus_chat") {
          if (!focusChat) throw new Error("请打开这个 IP 的对话。");
          focusChat(); reply({ result: { focused: true } }); return;
        }
        if (data.method === "surface.open_link") {
          // Opened by this document, from the person's own click inside the
          // frame, with no way back to the console from the new tab.
          const address = webAddress(data.params?.url);
          if (!address) throw new Error("只能打开网页地址。");
          window.open(address, "_blank", "noopener,noreferrer");
          reply({ result: { opened: true } });
          return;
        }
        if (data.method === "surface.subscribe") {
          // The page asks once; everything after this is pushed. The read
          // arguments are the page's choice of object inside its own declared
          // reads -- the tool and the constants are the declaration's, and the
          // server pins them (§7.130).
          const first = !subscribed.current;
          const all = declaredReads.map((_, at) => at);
          const before = readIndices.current ?? all;
          const selected = data.params?.read_indices;
          if (selected !== undefined && (!Array.isArray(selected)
            || selected.some(at => !Number.isInteger(at) || Number(at) < 0 || Number(at) >= declaredReads.length)))
            throw new Error("只能选择当前工作面已声明的读取项。");
          const nextIndices = selected === undefined ? readIndices.current
            : [...new Set(selected as number[])].sort((left, right) => left - right);
          const active = nextIndices ?? all;
          const changed = new Set<number>();
          const asked = data.params?.read_arguments;
          if (asked && typeof asked === "object") {
            if (subscribed.current && requestValue(asked) !== requestValue(readArguments.current)) {
              modelContextSequence.current += 1;
              reportScreenContent(currentShowing.current, undefined);
            }
            // A retarget starts a new observation. Its subscription cannot acknowledge an old object's ready value.
            const next = asked as Record<string, Record<string, unknown>>;
            reads.current = declaredReads.map((read, at) => {
              if (requestValue(next[String(at)] ?? {}) !== requestValue(readArguments.current[String(at)] ?? {})) {
                changed.add(at);
                return { index: at, tool: String((read as { tool?: string }).tool ?? ""), state: "pending" as const };
              }
              return reads.current[at] ?? { index: at, tool: String((read as { tool?: string }).tool ?? ""), state: "pending" as const };
            });
            readArguments.current = next;
          }
          if (!reads.current.length) {
            reads.current = declaredReads.map((read, at) =>
              ({ index: at, tool: String((read as { tool?: string }).tool ?? ""), state: "pending" as const }));
          }
          const wanted = active.filter(at => first || !before.includes(at) || changed.has(at));
          // Retargeting invalidates an older receipt even when this slot is
          // currently optional and its replacement will be read only later.
          changed.forEach(at => readCycles.current.set(at, (readCycles.current.get(at) ?? 0) + 1));
          for (const at of wanted) reads.current[at] = {
            index: at, tool: String((declaredReads[at] as { tool?: string }).tool ?? ""), state: "pending",
          };
          readIndices.current = nextIndices;
          subscribed.current = true;
          const whole = snapshot();
          said.current = whole;
          reply({ result: { ...whole, full: true } });
          if (wanted.length) void runReads(wanted);
          return;
        }
        if (data.method === "surface.refresh") {
          // One landing, one re-read. The page's runtime asks after a
          // submission it is following, and this host asks after one it
          // relayed (`refreshAfter`); they are the same fact about the same
          // command, and reading twice would put two rows through the read
          // door for one press. Whichever arrives first does it.
          const after = String(data.params?.after ?? "");
          if (after && refreshed.current.has(after)) { reply({ result: { refreshed: false } }); return; }
          if (after) refreshed.current.add(after);
          const asked = data.params?.indices;
          await runReads(Array.isArray(asked) ? asked.map(Number) : undefined);
          reply({ result: { refreshed: true } });
          return;
        }
        if (data.method === "surface.approvals_open") {
          // The card is the shell's UI, never the page's (§2 rule 4): all the
          // page may do is ask for the person to be taken to it.
          const wanted = String(data.params?.key ?? "");
          const pending = approvalsFeed.latest() ?? (await approvalsFeed.read()).pending;
          const card = pending.find(item => item.key === wanted || (item.effect_id && `effect:${item.effect_id}` === wanted));
          if (!current()) return;
          navigateWorkbench(card ? responseConversationHref(card, { approval: card.key }) : "#/chat?session=s-owner");
          reply({ result: { opened: true, found: Boolean(card) } });
          return;
        }
        if (data.method === "surface.act" || data.method === "surface.send_message") {
          // One press, under the name the page gave it. The same name sent
          // again is the same press: the server answers it with what it became.
          const method = data.method;
          const sent: Record<string, unknown> = { ...(data.params ?? {}) };
          sent.scope = sent.scope ?? handshake.current.scope;
          sent.context = sent.context ?? JSON.parse(contextKey);
          if (requestValue(sent.context) !== requestValue(JSON.parse(contextKey))) throw new Error("原操作属于另一份工作，暂不能在这里恢复。");
          const command = typeof sent.command_id === "string" ? sent.command_id : "";
          const label = method === "surface.send_message" ? "这项工作要求"
            : block.actions?.find(action => (action.binding ? action.key : action.tool) === sent.tool)?.label || "这次操作";
          const before = command ? presses.current.get(command) : undefined;
          if (command) presses.current.set(command, { ...before, method, params: sent, label, unknown: false, at: before?.at ?? Date.now() });
          pushState();
          inflight.current += 1;
          setPendingCount(inflight.current);
          try {
            const answer = await api.surfaceCall(panelKey, index, method, sent);
            const result = answer.result as { command_id?: unknown; obligation_id?: unknown };
            const named = typeof result.command_id === "string" ? result.command_id : command;
            if (named && !presses.current.has(named)) presses.current.set(named, { method, params: sent, label, at: Date.now() });
            if (named && typeof result.obligation_id === "string") accept(named, result.obligation_id);
            reply({ result: answer.result });
          } catch (err) {
            const failed = ApiError.fromUnknown(err);
            // Only a certain "not taken" ends a press: a first send the console
            // refused, or a press sent again that it read its record for and
            // refused (400). Anything else on a press sent again -- the console
            // still reading its record, no connection, an answer that did not
            // come back -- says nothing about the sending before it: the press
            // stays unknown, and is sent again under the same name.
            const refused = failed.status === 400 || (!before && failed.status >= 400 && failed.status < 500);
            const press = command ? presses.current.get(command) : undefined;
            // The console's own sentences name sockets, timeouts and the core:
            // written for whoever runs it, so an owner is told what happened
            // to the press instead -- not taken, or not known to be (§7.183).
            const said = developer || !failed.machine ? `${failed.title}：${failed.detail}`
              : refused ? `${label}没有提交。` : `还不确定${label}有没有交给 IP，没有重发。`;
            if (press && refused) {
              presses.current.delete(command);
              post({ method: "surface.receipt", params: { command_id: command, state: "refused",
                error: developer || !failed.machine ? failed.detail : said } });
            } else if (press) presses.current.set(command, { ...press, unknown: true });
            pushState();
            reply({ error: said, ...(refused ? {} : { code: "unknown" }) });
            // The page may have no room to say so; a refusal nobody sees is a
            // silent failure.  An unsure press is the page's to say: it was told
            // `unknown`, and a second sentence beside its own says nothing new.
            if (refused || developer) onToast(said, "critical");
          } finally {
            inflight.current -= 1;
            setPendingCount(inflight.current);
          }
          return;
        }
        if (data.method === "surface.error") {
          faultGeneration.current = documentGeneration.current;
          const detail = surfaceDiagnostic(data.params?.detail);
          setFault({ detail: detail && typeof detail === "object" ? detail as Record<string, unknown> : { message: String(detail || "界面报错") }, version: shownVersion.current });
        }
        if (data.method === "surface.save_state") {
          const state = data.params?.state ?? null;
          liveUiState.current = { key: draftKey, value: state };
          const savedContext = stateSaved.current?.(state) ?? JSON.parse(contextKey);
          writeDraft(`surface:${panelKey}:${index}:${JSON.stringify(savedContext)}`, state);
          // What the runtime's forms keep, said out loud: the typing, and --
          // once the durable write has answered -- whether it actually reached
          // disk. A draft that only ever reached this tab must not be drawn as
          // saved (§3).
          const kept = (state as { pa_drafts?: Record<string, { value?: unknown }> } | null)?.pa_drafts;
          if (kept) {
            const at = Date.now();
            const touched = Object.keys(kept);
            // In flight is not failed: `persisted: false` alone would draw the
            // 「草稿没能存下来」 line for the length of every round trip, on every
            // keystroke, whether or not the write is ever going to fail (§1).
            drafts.current = Object.fromEntries(Object.entries(kept)
              .map(([key, entry]) => [key, { ...entry, saved_at: at, persisted: false, writing: true }]));
            pushState();
            const write = ++draftWrites.current;
            api.surfaceState(panelKey, index, savedContext, state)
              .then(() => { if (write === draftWrites.current) settleDrafts(touched, true); })
              .catch(err => {
                if (write === draftWrites.current) settleDrafts(touched, false, ApiError.fromUnknown(err).detail);
                onToast("这份草稿只留在当前标签页，暂时没有保存下来。", "critical");
              });
            reply({ result: { saved: true, durable: false } });
            return;
          }
          // The tab's copy is the fast one; the durable one lives beside the
          // plugin's own data, so the draft is still there in a second tab and
          // after the browser is closed (D404). Answer the page as soon as the
          // tab holds it -- a page that waits on a round trip per keystroke is
          // a page that stutters -- and say so out loud if the durable write
          // is the one that failed.
          if (data.params?.durable) {
            await api.surfaceState(panelKey, index, savedContext, state);
            reply({ result: { saved: true, durable: true } });
            return;
          }
          reply({ result: { saved: true, durable: false } });
          api.surfaceState(panelKey, index, savedContext, state)
            .catch(() => onToast("这份草稿只留在当前标签页，暂时没有保存下来。", "critical"));
          return;
        }
        if (data.method === "surface.intent") {
          if (!prepareIntent) throw new Error("请在界面旁打开原对话，再准备这件工作。 ");
          const draft = await prepareSurfaceIntent(panelKey, index, {
            text: data.params?.text, read_key: data.params?.read_key, effect_id: data.params?.effect_id,
            reference: data.params?.reference,
            skill: data.params?.skill,
            command_id: `surface-intent-${crypto.randomUUID()}`,
            session_id: context.session_id || context.session || "s-owner", scope: handshake.current.scope,
          });
          if (!current()) return;
          prepareIntent(draft);
          // Sent from the composer under this name; its line appears when the record shows it.
          presses.current.set(draft.command_id, { method: "surface.send_message",
            params: { command_id: draft.command_id, text: draft.text }, label: "这项工作要求", at: Date.now() });
          reply({ result: { state: "draft", command_id: draft.command_id, session_id: draft.session_id } });
          return;
        }
        if (data.method === "surface.update_model_context") {
          const sequence = ++modelContextSequence.current;
          const selection = currentViewContext.current;
          const revision = contextRevision.current;
          const showing = currentShowing.current;
          // A pending or failed observation cannot keep the old object's name.
          reportScreenContent(showing, undefined);
          if (!await flushScreenIdentity()) throw new Error("当前页面身份尚未送达，请稍后重试。");
          if (!current() || showing !== currentShowing.current || !currentVisibility.current) {
            reply({ error: "页面已切换，请在当前页面重试。", code: "surface_paused" }); return;
          }
          const params = { ...data.params, context: JSON.parse(contextKey), scope: handshake.current.scope,
            showing: currentShowing.current };
          const answer = await api.surfaceCall(panelKey, index, data.method, params);
          if (!current() || !currentVisibility.current || showing !== currentShowing.current || selection !== currentViewContext.current
              || revision !== contextRevision.current || sequence !== modelContextSequence.current) {
            // A receipt this host retired must not become a healthy cached ACK in the guest.
            reply({ error: "页面已切换，请在当前页面重试。", code: "surface_paused" });
            return;
          }
          if ((answer.result as { accepted?: boolean; showing?: string } | null)?.accepted === true
              && (answer.result as { showing?: string }).showing === showing) {
            reportScreenContent(showing, data.params?.content);
          }
          reply({ result: answer.result });
          return;
        }
        if (data.method === "surface.open") {
          const navigation = workspaceNavigation({ workspace: handshake.current.navigation });
          const link = navigation?.links.find(item => item.label === data.params?.label);
          if (!link || !navigation) throw new Error("当前界面没有声明这个入口。 ");
          const saved = readUiState();
          let selected = savedNavigationContext(saved, navigation.state_context, JSON.parse(contextKey));
          if (data.params && Object.hasOwn(data.params, "context_values")) {
            // Before the page has saved any selection, its address is the current selection.
            selected = surfaceNavigationContext(navigation, link, saved === null ? JSON.parse(contextKey) : selected,
              data.params.context_values);
          }
          // Which Agent this page belongs to, from this document's own address.
          const agent = agentIdFrom(location.pathname);
          const href = recordLinkHref(link, sitsOn.current,
            { ...selected, ...(sitsOn.current ? JSON.parse(contextKey) : {}),
              panel_key: panelKey, return_panel: panelKey, ...(agent ? { agent_id: agent } : {}) });
          if (!href) throw new Error("这次结果尚未提供这个入口需要的资料。");
          navigateWorkbench(href);
          reply({ result: { opened: true, href } });
          return;
        }
        // The context is the shell's, never the guest's: it names the draft
        // file to read back, and a page must not be able to ask for another
        // view's draft by saying it is that view. An error report carries it
        // too: the note it becomes belongs in the page's own conversation.
        const params: Record<string, unknown> = data.method === "surface.ready" || data.method === "surface.tail" || data.method === "surface.error"
          ? { ...(data.params ?? {}), context: JSON.parse(contextKey), ...(data.method === "surface.ready" ? { showing: currentShowing.current } : {}) }
          : data.params ?? {};
        const answer = await api.surfaceCall(panelKey, index, data.method, params);
        if (data.method === "surface.rendered" && handshake.current.scope) {
          // A new document has actually read and painted without reporting a
          // runtime error. Retire the previous document's fault, not its draft.
          if (current() && faultGeneration.current !== generation) setFault(null);
          void Promise.resolve().then(() => api.surfacePreview(panelKey, { block: index, context: JSON.parse(contextKey), scope: handshake.current.scope,
            theme: effectiveTheme(), display: data.params?.display, ui_state: readUiState() })).catch(() => {});
        }
        // A read's answer is the page's alone: `pending`, `error` and `ready` are
        // the three states of one observation (§7.182), drawn by the page that
        // asked, and there is no command row behind any of them for this host
        // to register or poll.
        if (!current()) return;
        if (data.method === "surface.ready" && activeDraft.current !== draftKey) {
          // The page awaits this same id only once. Discard the stale result,
          // then use the latest context without abandoning its initialization.
          resumeReady.current(event);
          return;
        }
        if (data.method === "surface.tail" && !readScopeCurrent(answer.result)) {
          reply({ error: "界面读取来源已更新，请重新加载界面。", code: "surface_scope_changed" });
          return;
        }
        const result =
          data.method === "surface.ready"
            ? {
                ...(answer.result as Record<string, unknown>),
                context: JSON.parse(contextKey),
                // New documents restore the visible address; execution and pending remain bound to entry.
                view_context: JSON.parse(currentViewContext.current),
                visible: currentVisibility.current,
                // This tab's copy first -- it is what the person is typing in
                // right now -- and the durable one from the plugin's data
                // directory when this tab has none, which is every new tab and
                // every restarted browser (D404).
                ui_state: readUiState((answer.result as { ui_state?: unknown })?.ui_state ?? null),
                // The two facts the server cannot know.
                theme: effectiveTheme(),
                palette: document.documentElement.dataset.palette, motion: document.documentElement.dataset.motion || "full",
                size: {
                  width: box.current?.clientWidth ?? 0,
                  height: box.current?.clientHeight ?? 0,
                  compact,
                },
                // And, for a card's page, the record it sits on.
                ...(sitsOn.current === undefined ? {} : { record: sitsOn.current }),
              }
            : answer.result;
        reply({ result });
        if (data.method === "surface.ready") {
          handshake.current = answer.result as Record<string, unknown>;
          // What this page still has under way, off the record by page and
          // conversation: shown and followed here, never sent again.
          const pending = (answer.result as { pending?: PendingPress[] }).pending || [];
          for (const entry of pending) {
            if (presses.current.has(entry.command_id)) continue;
            const message = typeof entry.text === "string";
            presses.current.set(entry.command_id, {
              method: message ? "surface.send_message" : "surface.act",
              params: message ? { command_id: entry.command_id, text: entry.text }
                : { command_id: entry.command_id, tool: entry.action, arguments: entry.params ?? {},
                  ...(entry.object === undefined || entry.object === null ? {} : { object: entry.object }) },
              label: message ? "这项工作要求"
                : block.actions?.find(action => (action.binding ? action.key : action.tool) === entry.action)?.label || "这次操作",
              ...(entry.obligation_id ? { obligation_id: entry.obligation_id } : {}),
              unknown: !entry.obligation_id, at: Date.now(),
            });
            // Where it stands is the projection's word, and it came back with
            // it: a page reopened on a press waiting for an approval is not
            // told the press was just received.
            if (entry.obligation_id && entry.state && entry.state !== "received" && !reports.current[entry.obligation_id]) {
              reports.current[entry.obligation_id] = { obligation_id: entry.obligation_id, known: true, state: null,
                action_state: entry.state as CommandStatus["action_state"], settled_at: null, effects: [] };
            }
          }
          const lines = pending.filter(entry => entry.obligation_id && !savedSubmissions.current.some(item => item.id === entry.obligation_id));
          if (lines.length) {
            showSubmissions([...savedSubmissions.current, ...lines.map(entry => ({ id: entry.obligation_id!,
              label: presses.current.get(entry.command_id)?.label || "这次操作",
              ...(typeof entry.text === "string" ? { source: "surface_intent" as const } : {}) }))]);
          }
          // The drafts this page left on disk, which a brand-new tab has no
          // copy of. They are already written, so they are `persisted` -- and
          // they go in the snapshot rather than only in the handshake, because
          // the first full frame is what the page draws from.
          const stored = (answer.result as { ui_state?: { pa_drafts?: Record<string, SurfaceDraft> } | null }).ui_state?.pa_drafts;
          if (stored && typeof stored === "object") {
            const at = Date.now();
            drafts.current = { ...Object.fromEntries(Object.entries(stored)
              .map(([key, entry]) => [key, { ...entry, saved_at: entry.saved_at ?? at, persisted: true }])), ...drafts.current };
          }
          if (pending.length || (stored && typeof stored === "object")) pushState();
          Object.values(reports.current).forEach(status => post({ method: "surface.action", params: status }));
        }
      } catch (err) {
        if (!current()) return;
        if (data.method === "surface.ready" && activeDraft.current !== draftKey) {
          resumeReady.current(event);
          return;
        }
        const failed = ApiError.fromUnknown(err);
        reply({ error: `${failed.title}：${failed.detail}` });
        // A press refused before it was sent is visible in the shell too.
        if (data.method === "surface.act" || data.method === "surface.send_message") onToast(`${failed.title}：${failed.detail}`, "critical");
      }
    }
    resumeReady.current = event => { void onMessage(event); };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [panelKey, index, post, onToast, draftKey, contextKey, block.actions, visible, compact, showSubmissions, prepareIntent, focusChat, accept, snapshot, pushState, runReads, settleDrafts, declaredReads, context, readScopeCurrent, readUiState]);

  // Install the current relay before releasing deferred handshakes. A
  // same-document context change keeps the original ready promise alive.
  useEffect(() => {
    if (!visible) return;
    const waiting = [...deferredReady.current.values()];
    deferredReady.current.clear();
    waiting.forEach(item => {
      if (item.generation === documentGeneration.current && item.source === frame.current?.contentWindow)
        resumeReady.current(item.event);
    });
  }, [visible, contextKey]);

  // Theme: pushed, not polled. Two sources, because the shell has two — an
  // explicit choice on `<html data-theme>`, and the system setting when the
  // choice is "system" (`App.tsx` deletes the attribute in that case).
  useEffect(() => {
    const settle = () => setAppearance({ theme: effectiveTheme(), palette: document.documentElement.dataset.palette, motion: document.documentElement.dataset.motion || "full" });
    const watcher = new MutationObserver(settle);
    watcher.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-palette", "data-motion"] });
    const media_ = window.matchMedia?.("(prefers-color-scheme: dark)");
    media_?.addEventListener?.("change", settle);
    return () => {
      watcher.disconnect();
      media_?.removeEventListener?.("change", settle);
    };
  }, []);

  // Pushed on change *and* on load. The first push after a (re)assembly races
  // the iframe: React has set `srcdoc`, the browser has not finished parsing
  // it, and the guest's `message` listener does not exist yet — so a surface
  // opened in a dark shell painted light until the next theme change.
  const pushVisibility = useCallback(() => {
    post({ method: "surface.visibility", params: { visible, showing, recovery: contextRecovery.current,
      view_context: JSON.parse(currentViewContext.current) } });
  }, [post, visible, showing]);
  const pushSettings = useCallback(() => {
    pushVisibility();
    post({ method: "surface.theme", params: { theme: effectiveTheme(), palette: document.documentElement.dataset.palette, motion: document.documentElement.dataset.motion || "full" } });
    post({ method: "surface.size", params: { ...measuredBox(), compact } });
  }, [post, pushVisibility, compact, measuredBox]);

  useEffect(() => { pushVisibility(); pushState(); }, [pushVisibility, pushState]);
  useEffect(() => onScreenIdentityReady(() => {
    contextRecovery.current += 1;
    pushVisibility();
  }), [pushVisibility]);

  // The record a card's page sits on moved: the page hears it once, and only
  // when something in it changed (`stateFrame`).
  useEffect(() => { pushState(); }, [record, pushState]);

  // On the owner's screen while visible, under this showing (`lib/screen.ts`).
  useEffect(() => {
    if (!visible) return;
    const coordinates = JSON.parse(contextKey) as Record<string, string>;
    const session = coordinates.session_id || coordinates.session;
    return holdOnScreen(showing, { panel_key: panelKey, block: index, context: coordinates,
      ...(session ? { session_id: session } : {}) }, flushModelContext,
      { title: pageTitle || block.title || panelKey, context: JSON.parse(currentViewContext.current) });
  }, [visible, showing, panelKey, index, contextKey, flushModelContext]);
  useLayoutEffect(() => {
    const previous = displayedSelection.current;
    if (previous && (previous.showing !== showing || previous.context !== viewContextKey || previous.visible !== visible))
      modelContextSequence.current += 1;
    displayedSelection.current = { showing, context: viewContextKey, visible };
    displayScreenPage(showing, { title: pageTitle || block.title || panelKey, context: JSON.parse(viewContextKey) });
    pushVisibility();
  }, [showing, pageTitle, block.title, panelKey, viewContextKey, visible, pushVisibility]);
  useEffect(() => () => { contextFlushes.current.forEach(flush => flush.finish(false)); }, [showing]);
  useEffect(() => {
    modelContextSequence.current += 1;
  }, [showing]);

  useEffect(() => {
    post({ method: "surface.theme", params: appearance });
    pushState();
  }, [appearance, post, doc, pushState]);

  /**
   * The page was moved to another object.
   *
   * Everything the previous object's frames said is about the previous object,
   * so the reads start again as `pending` and the revision moves: a `tail`
   * answer still in flight for the old one is dropped on arrival (§1).
   */
  useEffect(() => {
    if (shownContext.current === contextKey) return;
    shownContext.current = contextKey;
    contextRevision.current += 1;
    reads.current = declaredReads.map((read, at) =>
      ({ index: at, tool: String((read as { tool?: string }).tool ?? ""), state: "pending" as const }));
    said.current = null;
    drafts.current = {};
    // Even a page with no declared reads/subscription must learn that its
    // host coordinate changed, so its model-context CAS can handshake anew.
    if (!subscribed.current) post({ method: "surface.state", params: { context: JSON.parse(contextKey), context_revision: contextRevision.current } });
    pushState();
    void runReads();
  }, [contextKey, declaredReads, pushState, runReads, post]);

  /**
   * The stream came back after a gap (`commandFeed.resync`).
   *
   * Whatever moved while nobody was listening was pushed to nobody, so the
   * page is told the whole of it again and the reads are asked once more --
   * the same answer `useCommandStatus` gives the shell's own components.
   */
  useEffect(() => {
    const stop = commandFeed.onResync(() => {
      contextRecovery.current += 1;
      pushVisibility();
      said.current = null;
      pushState();
      void runReads();
    });
    return stop;
  }, [pushState, runReads, pushVisibility]);


  // Size: the box's, not the window's. A page that lays itself out against the
  // window is wrong the moment the shell's sidebar opens.
  useEffect(() => {
    const target = box.current;
    if (!target || typeof ResizeObserver === "undefined") return;
    const watcher = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) {
        observed.current = { width: rect.width, height: rect.height };
        post({ method: "surface.size", params: { ...observed.current, compact } });
        pushState();
      }
    });
    watcher.observe(target);
    return () => watcher.disconnect();
  }, [post, compact, pushState]);

  /**
   * A submission has landed: read again what its declaration says it changed.
   *
   * What a page used to do with `setTimeout(refresh, 400)` after a click, in
   * the one place that knows when the submission actually landed rather than
   * when it was sent (§2 rule 8). `PanelAction.refresh` names the slots, so a
   * save re-reads the note rather than rebuilding the page; an action that
   * names none re-reads this block's reads, which is what "it changed
   * something here" means with nothing more specific said. Once per submission.
   */
  const refreshed = useRef(new Set<string>());
  const refreshAfter = useCallback((status: CommandStatus) => {
    if (!status.known || !SETTLED.includes(status.action_state || "")) return;
    if (refreshed.current.has(status.obligation_id)) return;
    const press = [...presses.current.values()].find(item => item.obligation_id === status.obligation_id);
    if (!press || press.method !== "surface.act") return;
    refreshed.current.add(status.obligation_id);
    const declared = block.actions?.find(action => (action.binding ? action.key : action.tool) === press.params.tool);
    void runReads(declared?.refresh?.length ? [...declared.refresh] : undefined);
  }, [block.actions, runReads]);

  // A tile has no room for a control of its own, and nothing on it is the page
  // a person reloads: the board's own refresh and the file watch below cover it.
  // It sits in the header beside quiet links, so it is a quiet control: reloading is not the page's main action.
  const reloadable = !compact && (doc !== null || Boolean(fault));
  const barred = toolsInBar && !compact && presentation === "page";
  useWorkTool("reload", reloadable ? { run: () => reloadNow.current(), busy: loading, disabled: pendingCount > 0 } : undefined, barred);
  const reloadControl = compact || barred || resourceFault ? null : doc !== null || fault ? <BusyButton className="btn btn-quiet" busyLabel="正在加载界面…" busy={loading}
    disabled={pendingCount > 0} onPress={reload}>重新加载界面</BusyButton> : null;

  return (
    <section className="block surface-block" data-block-kind="surface" data-block-index={index}
      data-compact={compact ? "" : undefined}>
      {presentation === "page" ? <SurfaceCardHead panelKey={panelKey} title={block.title} compact={compact} version={headVersion} inBar={barred} /> : null}
      {presentation === "page" ? (renderToolbar ? renderToolbar(reloadControl) : reloadControl ? <div className="surface-tools" role="group" aria-label="界面操作">{reloadControl}</div> : null) : null}
      {signedOut ? <div role="alert" className="surface-load-notice"><span>登录已失效，请重新登录后刷新。</span></div>
        : resourceFault ? <div role="alert" className="surface-load-notice"><span>{fault?.detail.element === "VIDEO" ? "视频资源" : fault?.detail.element === "AUDIO" ? "声音资源" : fault?.detail.element === "IMG" ? "一张图片" : "一项界面资源"}读取失败，已显示的其他内容仍可使用。</span>
        <button type="button" className="btn" disabled={loading || pendingCount > 0} onClick={reload}>重新读取当前页面</button></div>
        : fault ? <SurfaceRepair panelKey={panelKey} index={index} context={context} fault={fault} version={version}
        appliedVersion={appliedVersion} active={visible} onToast={onToast} onVerified={() => setFault(null)} /> : null}
      {error && doc !== null ? <div role="alert">界面更新没有加载成功，仍保留当前版本。{ApiError.fromUnknown(error).detail}
        <button type="button" className="btn" disabled={loading || pendingCount > 0} onClick={reload}>重试界面更新</button></div> : null}
      {version && appliedVersion && version !== appliedVersion ? (
        <div className="surface-update" role="status">
          <span>{pendingCount ? "正在核对当前操作，确认后即可换成新版界面。" : loading ? "正在换成新版界面。" : "界面有新版本，这次没能自己换过去。"}</span>
          <button type="button" className="btn" disabled={loading || pendingCount > 0} onClick={reload}>应用界面更新</button>
        </div>
      ) : null}
      {submissions.map((submission, at) => <SurfaceAction key={submission.id} submission={submission} earlier={at < submissions.length - 1}
        active={visible}
        onToast={onToast} onStatus={status => {
          const landed = status.known && status.action_state === "completed";
          if (landed && reports.current[status.obligation_id]?.action_state !== "completed") {
            setHeadVersion(n => n + 1);
          }
          reports.current[status.obligation_id] = status;
          // The press it belongs to is named, so a page can tell its own presses apart.
          const command = [...presses.current].find(([, press]) => press.obligation_id === status.obligation_id)?.[0];
          post({ method: "surface.action", params: command ? { ...status, command_id: command } : status });
          pushState();
          refreshAfter(status);
        }} />)}
      <div className={`surface-frame ${presentation === "embedded" ? "pa-open-group" : "pa-bounded-surface"}`} ref={box}
        style={flowHeight === undefined ? undefined : { height: flowHeight, minHeight: 0 }}>
        {error && doc === null ? <ErrorState error={error} onRetry={reload} /> : doc === null ? (
          <LoadingState label="装配这一页" />
        ) : (
          <iframe
            key={doc.generation}
            ref={frame}
            className="surface-iframe"
            title={block.title || panelKey}
            // The whole sandbox, said once: scripts and nothing else. No
            // same-origin, no popups, no downloads, no forms, no top
            // navigation (§7.130).
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            srcDoc={doc.srcdoc}
            onLoad={() => {
              loaded.current = true;
              setAppliedVersion(doc.version || version);
              setLoading(false);
              pushSettings();
            }}
          />
        )}
      </div>
      {developer && !compact ? <p className="surface-origin micro">
        这一页由插件自己写，跑在沙箱里；它能调的只有面板声明过的
        {(block.actions ?? []).length} 个工具。
      </p> : null}
    </section>
  );
}
